/**
 * 구독제 전환 안내 창 (2026-10-27 대표 지시 → 23시 대표 결정으로 '신청 안내'로 바꿈)
 *
 * 대표 결정: 동의는 원장이 설정 → 시설 정보에서 직접 [베이직 신청]하며 받는다.
 * 이 창은 동의를 받지 않고 그 신청 칸으로 안내만 한다 — [지금 신청하러 가기] / [나중에].
 * - 띄울지 말지는 서버가 정한다(GET /api/v1/owner/subscription-consent 의 required).
 *   베이직 신청을 마친 매장 · 총무(원장 대리) · 본부 시험 매장 · 이루는자리는 서버가 false 로 준다.
 * - [나중에]로 닫을 수 있지만 화면을 다시 열거나 다시 로그인하면 또 뜬다(신청할 때까지).
 * - 🔴 신청하지 않았다고 막는 기능은 없다(대표 결정 — 본부가 직접 연락).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import { authedFetch } from '../../../infrastructure/api/authedFetch';
import { ownerLayoutService } from '../services/OwnerLayoutService';
import { SUBSCRIPTION_PLAN_ANCHOR } from './SubscriptionPlanCard';
import { SubscriptionPrice, SubscriptionPriceBlock } from './SubscriptionPriceText';

interface ConsentStatus {
  required: boolean;
  effective_date: string;
  plan_name: string;
  fee_label: string;
  price?: SubscriptionPrice | null;
  support_email: string;
  organization_name?: string | null;
}

const CONSENT_URL = `${API_BASE_URL}/api/v1/owner/subscription-consent`;

// 원장 화면은 메뉴마다 화면 틀을 새로 띄운다 — [나중에]를 누른 뒤 메뉴를 옮길 때마다 다시 뜨지 않게
// 이 탭에서 한 번 닫았으면 기억한다(새로 고침·새로 로그인하면 다시 뜬다).
let dismissedInThisPage = false;

const formatKoreanDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${y}년 ${m}월 ${d}일`;
};

const SubscriptionConsentModal: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConsentStatus | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (dismissedInThisPage) return;
    authedFetch(CONSENT_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: ConsentStatus | null) => {
        if (!alive || !d) return;
        setStatus(d);
        setOpen(!!d.required);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!open || !status) return null;

  const effective = formatKoreanDate(status.effective_date);
  const close = () => {
    dismissedInThisPage = true;
    setOpen(false);
  };
  const goApply = () => {
    close();
    navigate(`${ownerLayoutService.getOwnerPath('/owner/facility-info')}#${SUBSCRIPTION_PLAN_ANCHOR}`);
    // 이미 시설 정보 화면이면 다시 그려지지 않으므로 직접 내려 준다
    setTimeout(() => {
      document.getElementById(SUBSCRIPTION_PLAN_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold text-gray-900">구독제 전환 안내</h2>
        <p className="text-sm text-gray-600 mt-1">
          {status.organization_name ? `${status.organization_name} 원장님, ` : '원장님, '}
          {effective}부터 구독제로 전환됩니다.
        </p>

        <div className="mt-4 rounded-lg border-2 border-blue-200 bg-blue-50 p-4">
          <p className="text-sm text-blue-700 font-semibold">{effective}부터</p>
          <SubscriptionPriceBlock planName={status.plan_name} price={status.price} feeLabel={status.fee_label} />
          <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc pl-5">
            <li>약정 없음 · 매월 10일 납부</li>
            <li>카카오 알림톡은 지금처럼 별도 — 건당 13원(부가세 별도)</li>
            <li>기능은 지금과 같습니다</li>
          </ul>
        </div>

        <p className="mt-4 text-sm text-gray-800 font-medium">
          설정 → 시설 정보에서 {status.plan_name} 신청을 해 주세요.
        </p>

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={close}
            className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            나중에
          </button>
          <button
            type="button"
            onClick={goApply}
            className="flex-1 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
          >
            지금 신청하러 가기
          </button>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          신청하실 때까지 로그인할 때마다 이 안내가 다시 표시됩니다. 궁금한 점은 {status.support_email} 또는 [문의하기]로 알려 주세요.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionConsentModal;
